import { Briefcase, Building2, Home, MapPin } from "lucide-react";
import { LocationType } from "@/api/auth/auth.types";

export { LocationType };

export type AddressView = "search" | "type" | "details";

export type FieldName =
  | "entrance"
  | "floor"
  | "apartmentNumber"
  | "buildingName"
  | "addressDetail";

export interface FieldConfig {
  name: FieldName;
  placeholder: string;
  required?: boolean;
}

export const LOCATION_TYPES = [
  { id: LocationType.Apartment, key: "apartment", icon: Building2 },
  { id: LocationType.House, key: "house", icon: Home },
  { id: LocationType.Office, key: "office", icon: Briefcase },
  { id: LocationType.Other, key: "other", icon: MapPin },
] as const;

export const TYPE_FIELDS: Partial<Record<LocationType, FieldConfig[]>> = {
  [LocationType.Apartment]: [
    { name: "entrance", placeholder: "business.profile.entrancePlaceholder" },
    { name: "floor", placeholder: "business.profile.floorPlaceholder" },
    {
      name: "apartmentNumber",
      placeholder: "business.profile.apartmentNumberPlaceholder",
    },
  ],
  [LocationType.Office]: [
    {
      name: "buildingName",
      placeholder: "business.profile.buildingNamePlaceholder",
    },
    { name: "entrance", placeholder: "business.profile.entrancePlaceholder" },
  ],
  [LocationType.Other]: [
    {
      name: "addressDetail",
      placeholder: "business.profile.addressDetailPlaceholder",
    },
  ],
};

export const OFFICE_METHODS = [
  { key: "toOffice" },
  { key: "toReception" },
] as const;

export const slideVariants = {
  enter: (d: string) => ({ opacity: 0, x: d === "f" ? 40 : -40 }),
  center: { opacity: 1, x: 0 },
  exit: (d: string) => ({ opacity: 0, x: d === "f" ? -40 : 40 }),
};

export const slideTransition = { duration: 0.15, ease: "easeOut" } as const;

export interface AddressPayload {
  streetLine1: string;
  streetLine2?: string;
  city: string;
  postalCode: string;
  countryCode: string;
  defaultShippingAddress: false;
  locationType: LocationType;
  entrance?: string;
  floor?: string;
  instructions?: string;
}

export function buildAddressPayload({
  place,
  locationType,
  fields,
  officeMethodLabel,
  instructions,
  operationCountry,
}: {
  place: {
    address: string;
    streetAddress: string;
    city: string;
    postalCode: string;
    countryCode: string;
  };
  locationType: LocationType;
  fields: Partial<Record<FieldName, string>>;
  officeMethodLabel: string | null;
  instructions: string;
  operationCountry?: string | null;
}): AddressPayload {
  const combined = [
    officeMethodLabel,
    locationType === LocationType.Other ? fields.addressDetail || null : null,
    instructions.trim() || null,
  ]
    .filter(Boolean)
    .join(". ");

  const streetLine2 = fields.apartmentNumber || fields.buildingName;

  return {
    streetLine1: place.streetAddress || place.address.split(",")[0].trim(),
    city: place.city,
    postalCode: place.postalCode,
    countryCode: place.countryCode || operationCountry || "FI",
    defaultShippingAddress: false,
    locationType,
    ...(streetLine2 && { streetLine2 }),
    ...(fields.entrance && { entrance: fields.entrance }),
    ...(fields.floor && { floor: fields.floor }),
    ...(combined && { instructions: combined }),
  };
}

export function isMissingRequired(
  requiredFields: FieldConfig[],
  fields: Partial<Record<FieldName, string>>,
  isOffice: boolean,
  officeMethod: string | null,
): boolean {
  return (
    requiredFields.some((f) => !fields[f.name]?.trim()) ||
    (isOffice && !officeMethod)
  );
}
